/**
 * ArchetypeFeatureVisual — visual proof for each feature claim on the
 * archetype landing pages (/for/:slug).
 *
 * Strategy
 * --------
 * Each "kind" maps to ONE of:
 *   1. A real screenshot of the live app saved under
 *      /public/screenshots/*.jpg (captured logged-in as demo@lumfi.app).
 *   2. A stylized mockup (when no dedicated app page exists for that
 *      capability yet — e.g. MRR/churn views are still on the roadmap).
 *
 * Real screenshots are wrapped in the same Frame chrome (traffic-light
 * dots + label) so the visual rhythm of the page stays consistent.
 *
 * The mapping + status of each visual is documented in
 * /app/memory/ARCHETYPE_FEATURE_VISUAL_REPORT.md.
 */
import {
  TrendingUp, ArrowDownRight, Bot, MessageCircle,
  Check, AlertCircle, Sparkles,
  Receipt, ShieldCheck, Flame, Clock,
} from "lucide-react";
import { TelegramBotMock } from "./TelegramBotMock";
import { FxConversionMock } from "./FxConversionMock";
import { ReceivableProbabilityMock } from "./ReceivableProbabilityMock";
import { SafeSpendMock } from "./SafeSpendMock";
import {
  GoalRingMock, ReserveChipMock, SourceSplitMock, ExportRowsMock,
  WeeklyChartMock, TimelineMock, WhatsappDigestMock, ScenarioWhatIfMock,
  ForecastChartMock, MrrMetricMock, ChurnAlertMock, MonthlyAllocationMock,
  MilestonePaymentsMock, PlatformAccountsMock, CommittedPaymentsMock,
  GapWeeklyMock, CalendarMarksMock,
} from "./ArchetypeMocks";
import { useExperiment } from "../../hooks/useExperiment";

function Frame({ children, label, badge }) {
  return (
    <div className="mt-6 rounded-2xl bg-white/95 border border-white/15 shadow-[0_18px_48px_-20px_rgba(0,0,0,0.45)] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200/80 bg-slate-50/80">
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
        </div>
        <div className="flex items-center gap-2">
          {badge && (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-md">
              <Sparkles className="h-2.5 w-2.5" /> {badge}
            </span>
          )}
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">{label || "Lumfi"}</span>
        </div>
      </div>
      <div className="text-slate-700">{children}</div>
    </div>
  );
}

/**
 * RealScreenshot — wraps a real app screenshot in a Frame and applies
 * a fixed aspect ratio + object-position so the visible portion focuses
 * on the relevant feature area instead of the sidebar.
 */
function RealScreenshot({ src, alt, label, objectPosition = "top left", aspectRatio = "16/10" }) {
  return (
    <Frame label={label}>
      <div className="bg-slate-100 overflow-hidden" style={{ aspectRatio }}>
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="w-full h-full object-cover"
          style={{ objectPosition }}
        />
      </div>
    </Frame>
  );
}

/* ============================================================== */
/* Real screenshots — captured logged-in as demo@lumfi.app         */
/* ============================================================== */

function FxMultiMockFrame() {
  // iter-211.2 — Replaced the full /accounts screenshot with a focused
  // conversion-only mock component (developer-flavoured: Stripe USD payout).
  return (
    <Frame label="FX · Multi-moneda">
      <div className="p-3">
        <FxConversionMock />
      </div>
    </Frame>
  );
}

function FxMultiScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/accounts.jpg"
      alt="Cuentas de efectivo multi-moneda en Lumfi"
      label="Cash · Multi-moneda"
      objectPosition="0% 0%"
    />
  );
}

function ReceivablesProbabilityFrame() {
  // iter-211.2 — Focused on the probability badge only.
  return (
    <Frame label="Cobros · Probabilidad">
      <div className="p-3">
        <ReceivableProbabilityMock />
      </div>
    </Frame>
  );
}

function ReceivablesScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/receivables.jpg"
      alt="Lista de cobros con probabilidad de pago en Lumfi"
      label="Cobros · Probabilidad"
      objectPosition="0% 0%"
    />
  );
}

function BotChatMockFrame() {
  // iter-211.2 — Replaced the static `/bots-guide` screenshot with a
  // real Telegram-style component.
  return (
    <Frame label="Telegram · Conversación">
      <div className="p-3">
        <TelegramBotMock />
      </div>
    </Frame>
  );
}

function BotChatScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/bots-guide.jpg"
      alt="Guía del bot de Telegram y WhatsApp de Lumfi"
      label="Telegram · Bot Guide"
      objectPosition="0% 0%"
    />
  );
}

function SafeSpendMockFrame() {
  // iter-211.2 — Focused on the "Seguro de usar / Riesgo mensual" KPI.
  return (
    <Frame label="Límite seguro · Mensual">
      <div className="p-3">
        <SafeSpendMock />
      </div>
    </Frame>
  );
}

function AlertGaugeScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/dashboard.jpg"
      alt="Panel con límite seguro semanal y riesgo del mes"
      label="Seguro de usar esta semana"
      objectPosition="0% 5%"
    />
  );
}

function WeeklyChartScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/feat-weekly-chart.jpg"
      alt="Reporte de cash-flow semanal en Lumfi"
      label="Reporte · Semanal"
      objectPosition="0% 0%"
    />
  );
}

function GoalRingScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/goals.jpg"
      alt="Metas con progress ring en Lumfi"
      label="Metas · Avance"
      objectPosition="0% 0%"
    />
  );
}

function CalendarMarksScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/calendar.jpg"
      alt="Calendario de movimientos en Lumfi"
      label="Calendario · Movimientos"
      objectPosition="0% 0%"
    />
  );
}

function ScenarioWhatIfScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/feat-scenario-whatif.jpg"
      alt="Escenarios what-if a 90 días en Lumfi"
      label="Escenarios · 90 días"
      objectPosition="0% 0%"
    />
  );
}

function MonthlyAllocationScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/feat-monthly-allocation.jpg"
      alt="Reparto mensual de ingresos en Lumfi"
      label="Reporte · Mensual"
      objectPosition="0% 30%"
    />
  );
}

function PlatformAccountsScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/accounts.jpg"
      alt="Cuentas por canal/plataforma en Lumfi"
      label="Cuentas · Por canal"
      objectPosition="0% 30%"
    />
  );
}

function ForecastChartScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/feat-forecast-chart.jpg"
      alt="Forecast a 90 días en Lumfi"
      label="Forecast · 90 días"
      objectPosition="0% 60%"
    />
  );
}

function CommittedPaymentsScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/payments.jpg"
      alt="Pagos comprometidos a futuro en Lumfi"
      label="Pagos · Comprometidos"
      objectPosition="0% 0%"
    />
  );
}

function MilestonePaymentsScreenshot() {
  // /receivables supports recurring + dated milestones — the timeline
  // is implicit in the row structure (anticipo, mid, cierre).
  return (
    <RealScreenshot
      src="/screenshots/receivables.jpg"
      alt="Cobros por hitos (anticipo + saldo) en Lumfi"
      label="Cobros · Anticipo + Saldo"
      objectPosition="0% 30%"
    />
  );
}

function TimelineScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/receivables.jpg"
      alt="Cobros recurrentes como hitos de proyecto en Lumfi"
      label="Proyecto · Hitos"
      objectPosition="0% 60%"
    />
  );
}

function ExportRowsScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/feat-export-rows.jpg"
      alt="Exportes mensuales por cliente en Lumfi"
      label="Reporte · Export CSV/PDF"
      objectPosition="0% 80%"
    />
  );
}

function ReserveChipScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/dashboard.jpg"
      alt="Reserva sugerida y desglose financiero en Lumfi"
      label="Reserva · Sugerida"
      objectPosition="0% 50%"
    />
  );
}

function SourceSplitScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/dashboard.jpg"
      alt="Desglose financiero por fuente en Lumfi"
      label="Desglose · Por fuente"
      objectPosition="0% 75%"
    />
  );
}

function GapWeeklyScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/feat-gap-weekly.jpg"
      alt="Cash gap por semana en escenarios de Lumfi"
      label="Gap · Por semana"
      objectPosition="0% 40%"
    />
  );
}

/* ============================================================== */
/* Mockups — features without a dedicated app screen yet           */
/* Listed in ARCHETYPE_FEATURE_VISUAL_REPORT.md                    */
/* ============================================================== */

function MrrMetricScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/feat-mrr-metric.jpg"
      alt="MRR neto proyectado en Lumfi"
      label="MRR · proyectado"
      objectPosition="0% 0%"
    />
  );
}

function ChurnAlertScreenshot() {
  return (
    <RealScreenshot
      src="/screenshots/feat-churn-alert.jpg"
      alt="Alerta de churn con impacto 90d en Lumfi"
      label="Alerta · churn"
      objectPosition="0% 0%"
    />
  );
}

function WhatsappDigestScreenshot() {
  // iter-212.2 — Digest preview now lives in the live app at
  // /email-preferences (token-gated) and a dashboard summary capture
  // ships alongside.
  return (
    <RealScreenshot
      src="/screenshots/feat-whatsapp-digest.jpg"
      alt="Resumen semanal por WhatsApp y email"
      label="WhatsApp · Resumen lunes"
      objectPosition="0% 0%"
    />
  );
}

/* ============================================================== */
/* A/B variant map · iter-212                                       */
/* Each entry exposes 2 variants. `useExperiment("landing-mock-vs-  */
/* screenshot")` picks one per visitor (sticky).                    */
/* ============================================================== */
const VARIANTS = {
  // 4 already-built mock variants (iter-211.2)
  "fx-multi":            { mock: () => <FxMultiMockFrame />,         screenshot: FxMultiScreenshot },
  "receivables-list":    { mock: () => <ReceivablesProbabilityFrame />, screenshot: ReceivablesScreenshot },
  "bot-chat":            { mock: () => <BotChatMockFrame />,         screenshot: BotChatScreenshot },
  "alert-gauge":         { mock: () => <SafeSpendMockFrame />,       screenshot: AlertGaugeScreenshot },
  // 16 new mocks (iter-212)
  "goal-ring":           { mock: () => <Frame label="Meta · Avance"><div className="p-3"><GoalRingMock /></div></Frame>,                screenshot: GoalRingScreenshot },
  "reserve-chip":        { mock: () => <Frame label="Reserva · Auto"><div className="p-3"><ReserveChipMock /></div></Frame>,            screenshot: ReserveChipScreenshot },
  "source-split":        { mock: () => <Frame label="Ingresos · Por fuente"><div className="p-3"><SourceSplitMock /></div></Frame>,     screenshot: SourceSplitScreenshot },
  "export-rows":         { mock: () => <Frame label="Reporte · Export"><div className="p-3"><ExportRowsMock /></div></Frame>,           screenshot: ExportRowsScreenshot },
  "weekly-chart":        { mock: () => <Frame label="Cash · Semanal"><div className="p-3"><WeeklyChartMock /></div></Frame>,            screenshot: WeeklyChartScreenshot },
  "timeline":            { mock: () => <Frame label="Proyecto · Hitos"><div className="p-3"><TimelineMock /></div></Frame>,             screenshot: TimelineScreenshot },
  "whatsapp-digest":     { mock: () => <Frame label="WhatsApp · Resumen"><div className="p-3"><WhatsappDigestMock /></div></Frame>,     screenshot: WhatsappDigestScreenshot },
  "scenario-whatif":     { mock: () => <Frame label="Escenarios · What-if"><div className="p-3"><ScenarioWhatIfMock /></div></Frame>,   screenshot: ScenarioWhatIfScreenshot },
  "forecast-chart":      { mock: () => <Frame label="Forecast · 90 días"><div className="p-3"><ForecastChartMock /></div></Frame>,      screenshot: ForecastChartScreenshot },
  "mrr-metric":          { mock: () => <Frame label="MRR · Proyectado"><div className="p-3"><MrrMetricMock /></div></Frame>,            screenshot: MrrMetricScreenshot },
  "churn-alert":         { mock: () => <Frame label="Alerta · Churn"><div className="p-3"><ChurnAlertMock /></div></Frame>,             screenshot: ChurnAlertScreenshot },
  "monthly-allocation":  { mock: () => <Frame label="Reparto · Mes"><div className="p-3"><MonthlyAllocationMock /></div></Frame>,       screenshot: MonthlyAllocationScreenshot },
  "milestone-payments":  { mock: () => <Frame label="Cobros · Anticipo+Saldo"><div className="p-3"><MilestonePaymentsMock /></div></Frame>, screenshot: MilestonePaymentsScreenshot },
  "platform-accounts":   { mock: () => <Frame label="Cuentas · Plataforma"><div className="p-3"><PlatformAccountsMock /></div></Frame>, screenshot: PlatformAccountsScreenshot },
  "committed-payments":  { mock: () => <Frame label="Pagos · Comprometidos"><div className="p-3"><CommittedPaymentsMock /></div></Frame>, screenshot: CommittedPaymentsScreenshot },
  "gap-weekly":          { mock: () => <Frame label="Cash gap · Semanal"><div className="p-3"><GapWeeklyMock /></div></Frame>,          screenshot: GapWeeklyScreenshot },
  "calendar-marks":      { mock: () => <Frame label="Calendario · Eventos"><div className="p-3"><CalendarMarksMock /></div></Frame>,    screenshot: CalendarMarksScreenshot },
};

/* legacy aliases — keep old keys working until landing data drops them */
const VISUALS = Object.fromEntries(Object.entries(VARIANTS).map(([k, v]) => [k, v.mock]));

export function ArchetypeFeatureVisual({ kind }) {
  const entry = VARIANTS[kind];
  const { variant, isLoading } = useExperiment("landing-mock-vs-screenshot", { defaultVariant: "mock" });
  if (!entry) return null;
  // While the variant resolves (one network roundtrip), render the mock
  // so layout doesn't jump. The exposure event fires asynchronously.
  if (isLoading) {
    const Mock = entry.mock;
    return <Mock />;
  }
  const Comp = variant === "screenshot" ? entry.screenshot : entry.mock;
  return <Comp />;
}

export const VISUAL_KEYS = Object.keys(VISUALS);

// Suppress unused-import warnings for icons kept available for future
// mockup variants (Bot, ShieldCheck, Flame, Clock, Check, Receipt,
// ArrowDownRight, Sparkles).
export const _RESERVED_ICONS = { Bot, ShieldCheck, Flame, Clock, Check, Receipt, ArrowDownRight, Sparkles };
